import React, { useState } from 'react';
import { Property } from '../../types/property.ts';
import PropertyDetails from './PropertyDetails.tsx';
import { formatCurrency } from '../../utils/formatters.ts';

interface PropertyListProps {
  properties: Property[];
  onEdit: (property: Property) => void;
  onDelete: (id: string) => Promise<void>;
}

const PropertyList: React.FC<PropertyListProps> = ({ properties, onEdit, onDelete }) => {
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);

  const groupedProperties = {
    rented: properties.filter(p => p.status === 'Rented'),
    vacant: properties.filter(p => p.status === 'Vacant')
  };

  const getStatusClass = (status: string) => {
    return `status-badge status-${status.toLowerCase()}`;
  };

  return (
    <>
      <div className="property-groups">
        <div className="property-group">
          <div className="group-header">
            <h2>Rented Properties ({groupedProperties.rented.length})</h2>
          </div>
          <div className="property-list">
            {groupedProperties.rented.map((property) => (
              <div key={property.id} className="property-card">
                <div 
                  className="property-header clickable"
                  onClick={() => setSelectedProperty(property)}
                >
                  <h3 className="property-title">{property.name}</h3>
                  <span className={getStatusClass(property.status)}>
                    <span className="status-dot"></span>
                    {property.status}
                  </span>
                </div>
                
                <div className="property-details">
                  <p className="floor-unit">
                    {property.floor_unit ? 
                      `📍 ${property.floor_unit}` : 
                      <span className="text-muted">No floor/unit specified</span>
                    }
                  </p>
                  <p>📏 {property.area} sq.ft</p>
                  <p>💰 {formatCurrency(property.planned_rent)}</p>
                </div>

                <div className="property-actions">
                  <button onClick={(e) => {
                    e.stopPropagation();
                    onEdit(property);
                  }} className="btn-secondary">
                    Edit
                  </button>
                </div>
              </div>
            ))}
            {groupedProperties.rented.length === 0 && (
              <div className="no-properties">No rented properties found</div>
            )}
          </div>
        </div>

        <div className="property-group">
          <div className="group-header">
            <h2>Vacant Properties ({groupedProperties.vacant.length})</h2>
          </div>
          <div className="property-list">
            {groupedProperties.vacant.map((property) => (
              <div key={property.id} className="property-card">
                <div 
                  className="property-header clickable"
                  onClick={() => setSelectedProperty(property)}
                >
                  <h3 className="property-title">{property.name}</h3>
                  <span className={getStatusClass(property.status)}>
                    <span className="status-dot"></span>
                    {property.status}
                  </span>
                </div>
                
                <div className="property-details">
                  <p className="floor-unit">
                    {property.floor_unit ? 
                      `📍 ${property.floor_unit}` : 
                      <span className="text-muted">No floor/unit specified</span>
                    }
                  </p>
                  <p>📏 {property.area} sq.ft</p>
                  <p>💰 {formatCurrency(property.planned_rent)}</p>
                </div>

                <div className="property-actions">
                  <button onClick={(e) => {
                    e.stopPropagation();
                    onEdit(property);
                  }} className="btn-secondary">
                    Edit
                  </button>
                </div>
              </div>
            ))}
            {groupedProperties.vacant.length === 0 && (
              <div className="no-properties">No vacant properties found</div>
            )}
          </div>
        </div>
      </div>

      {selectedProperty && (
        <PropertyDetails
          property={selectedProperty}
          onClose={() => setSelectedProperty(null)}
        />
      )}
    </>
  );
};

export default PropertyList;
